import { Card } from '@app/features/analytics/components';
import { ErrorScreen } from '@app/features/analytics/components/error-states/error-screen';
import { NoFilterResults } from '@app/features/analytics/components/error-states/no-filter-results';
import { NoResults } from '@app/features/analytics/components/error-states/no-results';
import { useAnalytics } from '@app/features/analytics/hooks/use-analytics';
import { useFetch } from '@app/features/analytics/hooks/use-fetch';
import { useSpaceContext } from '@app/features/analytics/hooks/use-space-context';
import { useAnalyticsContext } from '@app/features/analytics/providers/analytics-provider';
import { fetchEvents } from '@app/features/analytics/services/endpoints';
import { formatDate } from '@app/features/analytics/utils';
import { useReadDashboardSharedState } from '@hopin-team/dashboard-shared-react';
import { useTranslation } from '@hopin-team/provider-translation';
import React, { useEffect, useState } from 'react';

import { EventType } from '../../events/types';
import LoaderSkeleton from '../loader/loader';

const ListView = ({ data }: { data: EventType[] }) => (
  <div>
    {data?.map((item, index) => (
      <div
        className="relative flex justify-between break-words border-t border-neutral-b4 pt-3 pb-3 leading-6 first:border-t-0"
        key={index}
        data-testid="listitem"
      >
        <div className="font-inter truncate mr-2 text-[13px] font-medium">
          {item.name}
        </div>
        <div className="flex-1 text-nowrap font-inter mr-4 text-[12px] font-normal text-neutral-b1">
          {formatDate(item.startTime) + ' > ' + formatDate(item.endTime)}
        </div>
        <div className="text-nowrap text-right font-inter text-[13px] font-medium">
          {item.attendeesNumber}
        </div>
      </div>
    ))}
  </div>
);

export const MostVisitedEvents = () => {
  const { state, dispatch } = useAnalyticsContext();
  const { track } = useAnalytics();
  const orgId: string = useReadDashboardSharedState('org_id') || '';
  const { spaceExternalId } = useSpaceContext();
  const config = {
    dateRange: state.dateRange,
    startDate: state.startDate,
    endDate: state.endDate,
    eventTypes: state.eventTypes,
    name: 'attendeesNumber',
    direction: 'desc',
    perPage: '5',
    selectedEvents: state.selectedEvents,
    spaceExternalId,
  };
  const { data, isLoading, isError } = useFetch(
    config,
    `mostVisitedEvents-${orgId}`,
    fetchEvents,
  );
  const { t } = useTranslation();
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const result = {
    eventsData: data?.events || [],
    pagination: data?.pagination || {},
  };
  const hasData = result.eventsData.length > 0;
  const hasNextPage = result.pagination.totalCount > 5;
  if (hasData) {
    track('ola_overview_most_visited_events_view_all_click', {});
  }

  const onViewAll = () => {
    dispatch({
      type: 'analytics tab updated',
      payload: { index: 1, tabName: 'events' },
    });
    dispatch({
      type: 'update events sort key and direction',
      payload: { name: 'attendeesNumber', direction: 'desc' },
    });
    dispatch({
      type: 'update search text',
      payload: { searchText: '' },
    });
  };

  useEffect(() => {
    if (!isLoading) {
      setIsInitialLoad(false);
    }
  }, [isLoading]);

  return (
    <Card
      heading={t('analytics.most-visited-events')}
      tooltip={t('analytics.most-visited-events-tooltip')}
      subHeading={t('analytics.most-visited-events-content')}
      loading={isLoading}
      showFooter={hasData && hasNextPage && !isError}
      onViewAll={onViewAll}
    >
      <>
        {isError && !isLoading && <ErrorScreen showButton={false} />}
        {!isError && !isLoading && !hasData && isInitialLoad && <NoResults />}
        {!isError && !isLoading && !hasData && !isInitialLoad && (
          <NoFilterResults />
        )}
        {!isError && !isLoading && hasData && (
          <ListView data={result.eventsData} />
        )}
        {isLoading && <LoaderSkeleton hasData={hasData} />}
      </>
    </Card>
  );
};
